import React, {
  forwardRef,
  useImperativeHandle,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import clsx from "clsx";
import { useSelector } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Dialog from "src/components/Dialog";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import OutlinedInput from "@material-ui/core/OutlinedInput";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import LinearProgress from "@material-ui/core/LinearProgress";
import Typography from "@material-ui/core/Typography";
import InputAdornment from "@material-ui/core/InputAdornment";
import Switch from "@material-ui/core/Switch";
import CSVFormattedXGrid from "src/components/CSVFormattedXGrid";
import { createTableToolbar } from "src/components/TableToolbar";
import CircularProgress from "@material-ui/core/CircularProgress";

import { formatCurrencyValue } from "src/utils/formatter";
import { isFloatEqual } from "src/utils/number";
import {
  getReconciliationContext,
  updateReconciliation,
  type ReconciliationContextPayload,
} from "src/apiService/modules/reconciliations";
import { useRecordEvent } from "src/hooks/useRecordEvent";
import NumberField from "src/components/NumberField";
import useMountEffect from "src/utils/useMountEffect";
import { effectiveUserTimeZoneSelector } from "src/store/system/selector";
import {
  formatZonedDate,
  tryYmdStringToEndOfDayUtc,
  utcToDateOnlyPickerWallDate,
} from "src/utils/timezone";
import type { Reconciliation } from "src/interfaces/reconciliation.interface";
import type { BankAccountWithReconciliations } from "./types";
import { useOpenExternalEditor } from "./useOpenExternalEditor";
function reconciliationEndingDate(
  reconciliation: { date: string },
  timeZone: string
) {
  return tryYmdStringToEndOfDayUtc(reconciliation.date, timeZone);
}

const useProgressInputComponentStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    "& > * + *": {
      marginLeft: theme.spacing(1),
    },
  },
  progress: {
    flex: 1,
  },
  text: {
    minWidth: 35,
  },
}));

const ProgressInputComponent = forwardRef<HTMLDivElement, any>(
  ({ value, className, inputRef, loading }, ref) => {
    const classes = useProgressInputComponentStyles();
    useImperativeHandle(inputRef, () => ({
      value,
      nodeName: "INPUT",
    }));
    return (
      <div ref={ref} className={clsx(className, classes.root)}>
        <div className={classes.progress}>
          <LinearProgress
            variant={loading ? "indeterminate" : "determinate"}
            value={loading ? undefined : value}
          />
        </div>
        <div className={classes.text}>
          <Typography variant="body2" color="textSecondary">
            {loading ? "--" : `${Math.round(value)}%`}
          </Typography>
        </div>
      </div>
    );
  }
);

const ReconciliationToolbar = createTableToolbar();

const useStyles = makeStyles((theme) => ({
  content: {
    height: "calc(90vh - 64px)",
    display: "flex",
    flexDirection: "column",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gridTemplateRows: "auto auto",
    "& > div": {
      padding: theme.spacing(0, 1),
    },
  },
  table: {
    margin: theme.spacing(1, 0, 0, 0),
    flex: 1,
  },
  tableToolbar: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.spacing(1),
  },
}));

interface ContentProps {
  onClose: (dialog?: "manual-reconciliation") => void;
  bankAccount: BankAccountWithReconciliations;
  reconciliationIndex: number;
  ready: boolean;
  onReconciliationUpdated: (reconciliation: Reconciliation) => void;
}

function Content({
  bankAccount,
  onClose,
  reconciliationIndex,
  ready,
  onReconciliationUpdated,
}: ContentProps) {
  const recordEvent = useRecordEvent();

  const timeZone = useSelector(effectiveUserTimeZoneSelector);
  const openExternalEditor = useOpenExternalEditor();
  useMountEffect(() => {
    recordEvent({
      type: "feature-adoption",
      feature: "manual-reconciliation-initiated",
    });
  });
  const reconciliation = bankAccount.reconciliations[reconciliationIndex];
  const reconciliationRef = useRef(reconciliation);
  reconciliationRef.current = reconciliation;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [contextData, setContextData] = useState<
    (ReconciliationContextPayload & { version: number }) | null
  >(null);
  const rows = (contextData?.rows || []).map((row) => ({
    ...row,
    date: new Date(row.date),
  }));
  const matchedBalance = rows.reduce(
    (sum, row) => sum + (row.matched ? row.amount : 0),
    0
  );
  const statementBalance = Number(
    contextData?.balance ?? (reconciliation.balance || 0)
  );
  const endingBalance = (contextData?.startingBalance || 0) + matchedBalance;
  const difference = Math.abs(endingBalance - statementBalance);
  const matchedRows = rows.reduce((c, r) => (r.matched ? c + 1 : c), 0);
  const gridLoading = loading || !contextData;
  const columns = useMemo(
    () => [
      {
        field: "id",
        hide: true,
      },
      {
        field: "date",
        headerName: "Date",
        type: "date",
        width: 200,
        valueGetter: (params) => {
          const value = params.row?.date;
          const date =
            value instanceof Date
              ? value
              : typeof value === "string"
              ? new Date(value)
              : new Date(NaN);
          return utcToDateOnlyPickerWallDate(date, timeZone);
        },
        valueFormatter: (params) => {
          const value = params.row?.date;
          const date =
            value instanceof Date
              ? value
              : typeof value === "string"
              ? new Date(value)
              : undefined;
          return date ? formatZonedDate(date, timeZone) : "";
        },
      },
      {
        field: "timestamp",
        headerName: "Timestamp",
        width: 200,
        hide: true,
        valueGetter: (params) => params.row.date?.toISOString?.() ?? "",
      },
      {
        field: "charge_description",
        headerName: "Charge Description",
        width: 200,
        type: "string",
      },
      {
        field: "description",
        headerName: "Description",
        description: "A note to add information about the transaction.",
        flex: 1,
      },
      {
        field: "amount",
        headerName: "Amount",
        width: 200,
        type: "number",
        valueFormatter: (params) => formatCurrencyValue(params.value as number),
      },
      {
        field: "matched",
        headerName: "Matched",
        type: "boolean",
        renderCell: (params) => {
          return (
            <Switch
              checked={Boolean(params.value)}
              color="primary"
              onChange={(event) => {
                params.api.setEditCellValue(
                  {
                    id: params.id,
                    field: params.field,
                    value: event.target.checked,
                  },
                  event
                );
                params.api.commitCellChange(
                  { id: params.id, field: params.field },
                  event
                );
                params.api.setCellMode(params.id, params.field, "view");
              }}
            />
          );
        },
      },
    ],
    [timeZone]
  );

  const sendUpdate = useCallback(
    async (
      expectedVersion: number,
      payload: Partial<{
        balance: number;
        transactions: { type: "sale" | "transaction"; id: string }[];
      }>
    ) => {
      try {
        const response = await updateReconciliation(reconciliation.id, payload);
        onReconciliationUpdated({
          ...reconciliationRef.current,
          id: response.id,
          open: response.open,
          balance: response.balance,
          bank_account_id: response.bank_account_id,
          transactions: response.transactions,
        });
        if (Array.isArray(response.affectedReconciliations)) {
          for (const affected of response.affectedReconciliations) {
            onReconciliationUpdated({
              ...reconciliationRef.current,
              id: affected.id,
              open: affected.open,
              balance: affected.balance,
              bank_account_id: affected.bank_account_id,
            });
          }
        }
        setContextData((current) => {
          if (!current || current.version !== expectedVersion) return current;
          return {
            ...response,
            version: current.version,
          };
        });
        if (!response.open) {
          onCloseRef.current();
        }
      } catch (_error) {
        // Intentionally keep local edits on request failures.
      }
    },
    [onReconciliationUpdated, reconciliation.id]
  );

  const toTransactionsPayload = useCallback(
    (sourceRows: ReconciliationContextPayload["rows"]) =>
      sourceRows.reduce<{ type: "sale" | "transaction"; id: string }[]>(
        (acc, row) => {
          if (!row.matched) return acc;
          if (
            !acc.find(
              (t) => t.type === row.collection && t.id === row.collection_id
            )
          ) {
            acc.push({ type: row.collection, id: row.collection_id });
          }
          return acc;
        },
        []
      ),
    []
  );
  const applyOptimisticMatchedChange = useCallback(
    async (
      updater: (
        currentRows: ReconciliationContextPayload["rows"]
      ) => ReconciliationContextPayload["rows"]
    ) => {
      let request:
        | {
            version: number;
            transactions: { type: "sale" | "transaction"; id: string }[];
          }
        | undefined;
      setContextData((current) => {
        if (!current) return current;
        const nextRows = updater(current.rows);
        const nextVersion = current.version + 1;
        request = {
          version: nextVersion,
          transactions: toTransactionsPayload(nextRows),
        };
        return {
          ...current,
          rows: nextRows,
          transactions: request.transactions,
          version: nextVersion,
        };
      });
      if (!request) return;
      await sendUpdate(request.version, {
        transactions: request.transactions,
      });
    },
    [sendUpdate, toTransactionsPayload]
  );

  const fetchContext = useCallback(async () => {
    const next = await getReconciliationContext(reconciliation.id);
    setContextData((current) => ({
      ...next,
      version: (current?.version ?? -1) + 1,
    }));
  }, [reconciliation.id]);

  useEffect(() => {
    let canceled = false;
    const loadContext = async () => {
      setLoading(true);
      try {
        const next = await getReconciliationContext(reconciliation.id);
        if (canceled) return;
        setContextData({
          ...next,
          version: 0,
        });
      } finally {
        if (!canceled) setLoading(false);
      }
    };
    void loadContext();
    return () => {
      canceled = true;
    };
  }, [reconciliation.id]);

  const openEditorAndRefresh = useCallback(
    async (entity: "sale" | "transaction", id: string) => {
      const result = await openExternalEditor(entity, id);
      if (result.status !== "updated") return;
      setLoading(true);
      try {
        if (result.saved) {
          await result.saved;
        }
        await fetchContext();
      } finally {
        setLoading(false);
      }
    },
    [fetchContext, openExternalEditor]
  );

  return (
    <>
      <DialogTitle>
        {bankAccount.name} -{" "}
        {formatZonedDate(
          reconciliationEndingDate(reconciliation, timeZone),
          timeZone
        )}
      </DialogTitle>
      <DialogContent className={classes.content}>
        <div className={classes.grid}>
          <div>
            <NumberField
              allowNegative
              autoComplete="off"
              margin="dense"
              fullWidth
              label="Ending Balance (Statement)"
              value={statementBalance}
              variant="outlined"
              name="balance"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
              onChange={(e) => {
                const value = e.target.value;
                if (value !== undefined && (value as any) !== "") {
                  const nextBalance = Number(value);
                  if (!Number.isFinite(nextBalance)) return;
                  let request:
                    | {
                        version: number;
                        shouldClose: boolean;
                      }
                    | undefined;
                  setContextData((current) => {
                    if (!current) return current;
                    const nextVersion = current.version + 1;
                    const periodNet = current.rows.reduce(
                      (sum, row) => sum + row.amount,
                      0
                    );
                    const open = !isFloatEqual(
                      (current.startingBalance || 0) + periodNet,
                      nextBalance
                    );
                    request = {
                      version: nextVersion,
                      shouldClose: !open,
                    };
                    return {
                      ...current,
                      balance: nextBalance,
                      open,
                      version: nextVersion,
                    };
                  });
                  if (!request) return;
                  void sendUpdate(request.version, { balance: nextBalance });
                  if (request.shouldClose) {
                    onReconciliationUpdated({
                      ...reconciliationRef.current,
                      open: false,
                      balance: nextBalance,
                    });
                    onCloseRef.current();
                  }
                }
              }}
            />
          </div>
          <div>
            <TextField
              value={
                gridLoading
                  ? ""
                  : (isFloatEqual(difference, 0) ? 0 : difference).toFixed(2)
              }
              placeholder={gridLoading ? "Loading..." : undefined}
              inputProps={{ readOnly: true }}
              margin="dense"
              variant="outlined"
              fullWidth
              label="Difference"
              disabled={gridLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
                endAdornment: gridLoading ? (
                  <InputAdornment position="end">
                    <CircularProgress size={16} />
                  </InputAdornment>
                ) : undefined,
              }}
            />
          </div>
          <div>
            <TextField
              value={
                gridLoading
                  ? ""
                  : endingBalance != null
                  ? (endingBalance === 0 ? 0 : endingBalance).toFixed(2)
                  : endingBalance
              }
              placeholder={gridLoading ? "Loading..." : undefined}
              inputProps={{ readOnly: true }}
              margin="dense"
              variant="outlined"
              fullWidth
              label="Ending Balance (My Reseller Genie)"
              disabled={gridLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
                endAdornment: gridLoading ? (
                  <InputAdornment position="end">
                    <CircularProgress size={16} />
                  </InputAdornment>
                ) : undefined,
              }}
            />
          </div>
          <div>
            <FormControl margin="dense" variant="outlined" fullWidth>
              <InputLabel variant="outlined">Matched Transactions</InputLabel>
              <OutlinedInput
                inputComponent={ProgressInputComponent}
                inputProps={{ loading: gridLoading }}
                margin="dense"
                fullWidth
                label="Matched Transactions"
                value={(matchedRows * 100) / (rows.length || 1) || 0}
              />
            </FormControl>
          </div>
        </div>
        <div className={classes.table}>
          {ready ? (
            <CSVFormattedXGrid
              loading={gridLoading}
              disableSelectionOnClick
              headerHeight={35}
              rowHeight={35}
              rows={rows}
              components={{ Toolbar: ReconciliationToolbar }}
              columns={columns}
              onCellDoubleClick={(e) => {
                if (e.row.collection === "transaction") {
                  void openEditorAndRefresh("transaction", e.row.collection_id);
                  return;
                }
                if (e.row.collection === "sale") {
                  void openEditorAndRefresh("sale", e.row.collection_id);
                  return;
                }
              }}
              onCellEditCommit={(params, event, { api }) => {
                if (params.field === "matched") {
                  const row = api.getRow(params.id);
                  void applyOptimisticMatchedChange((currentRows) =>
                    currentRows.map((r) =>
                      r.collection === row.collection &&
                      r.collection_id === row.collection_id
                        ? { ...r, matched: Boolean(params.value) }
                        : r
                    )
                  );
                }
              }}
            />
          ) : null}
        </div>
        <div className={classes.tableToolbar}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              if (matchedRows === rows.length) {
                void applyOptimisticMatchedChange((currentRows) =>
                  currentRows.map((r) => ({ ...r, matched: false }))
                );
              } else {
                void applyOptimisticMatchedChange((currentRows) =>
                  currentRows.map((r) => ({ ...r, matched: true }))
                );
              }
            }}
            disabled={loading}
          >
            {matchedRows === rows.length ? "Deselect All" : "Select All"}
          </Button>
        </div>
      </DialogContent>
    </>
  );
}

function ManualReconciliationDialog({
  open,
  onClose,
  bankAccount,
  reconciliationIndex,
  onReconciliationUpdated,
}: Omit<ContentProps, "reconciliationIndex" | "ready"> &
  Partial<Pick<ContentProps, "reconciliationIndex">> & { open: boolean }) {
  const index =
    reconciliationIndex !== undefined
      ? reconciliationIndex
      : bankAccount.reconciliations.length - 1;
  const previousReconciliation = bankAccount.reconciliations[index - 1];
  const reconciliation = bankAccount.reconciliations[index];
  const ref = useRef<ReactNode | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!open) {
      setReady(false);
    }
  }, [open]);

  if (open) {
    ref.current = (
      <Content
        key={index}
        bankAccount={bankAccount}
        onClose={onClose}
        reconciliationIndex={index}
        ready={ready}
        onReconciliationUpdated={onReconciliationUpdated}
      />
    );
  }
  return (
    <Dialog
      open={open && !!previousReconciliation && !!reconciliation}
      onClose={() => onClose()}
      maxWidth="lg"
      fullWidth
      TransitionProps={{
        onEntered: () => setReady(true),
      }}
    >
      {ref.current}
    </Dialog>
  );
}

export default ManualReconciliationDialog;
